import PlateService from 'business/services/PlateService';
import ScreenService from 'business/services/ScreenService';
import EditBarcodeResponseModalBody from 'components/sidePanel/projectSidePanel/EditBarcodeResponseModalBody';
import { useModalHandlers } from 'components/modal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { updatePlateBarcodes } from 'redux/reducers/experimentDataReducer';
import EditBarcodeBulkModalBody from './EditBarcodeBulkModalBody';
import {
  buildEditBarcodeRows,
  buildEditBarcodeRowsFromScreenNodeInfo,
  mergePlateBarcodesFromPlateInfo,
} from './editBarcodeUtils';
import { EditBarcodeBulkModalHandlersRef, EditBarcodeRow } from './constants';

const plateService = new PlateService();
const screenService = new ScreenService();

async function loadScreenEditBarcodeRows(treeNodeId: number): Promise<EditBarcodeRow[]> {
  const [screenNodeInfo, plateInfoList] = await Promise.all([
    screenService.getScreenNodeInfo(treeNodeId),
    plateService.getExperimentPlatesInfo(treeNodeId),
  ]);

  const plateInfoById = new Map(plateInfoList.map((plate) => [plate.id, plate]));

  return buildEditBarcodeRowsFromScreenNodeInfo(screenNodeInfo, plateInfoById);
}

async function loadExperimentEditBarcodeRows(
  treeNodeId: number | undefined,
  plateDto: Parameters<typeof buildEditBarcodeRows>[0],
  screenLots: Parameters<typeof buildEditBarcodeRows>[1],
): Promise<EditBarcodeRow[]> {
  if (!treeNodeId || !plateDto?.length) {
    return buildEditBarcodeRows(plateDto, screenLots);
  }

  const plateInfoList = await plateService.getExperimentPlatesInfo(treeNodeId);
  return buildEditBarcodeRows(
    mergePlateBarcodesFromPlateInfo(plateDto, plateInfoList),
    screenLots,
  );
}

export function useEditBarcodeBulkModal(isEditBarcodeAllowed: boolean) {
  const { showModal, hideModal, showLoadingModal } = useModalHandlers();
  const dispatch = useRMDispatch();
  const expData = useRMSelector((state) => state.experiment.expData);
  const { pathname } = useLocation();
  const isScreenMode = pathname.includes('/screen');
  const [screenEditBarcodeRows, setScreenEditBarcodeRows] = useState<
    EditBarcodeRow[] | null
  >(null);
  const showEditBarcodeModalRef = useRef<() => void>(() => {});
  const modalHandlersRef = useRef<EditBarcodeBulkModalHandlersRef>({
    getUpdates: () => [],
  });

  useEffect(() => {
    if (!isScreenMode || !expData?.treeNodeId) {
      setScreenEditBarcodeRows(null);
      return;
    }

    let cancelled = false;

    loadScreenEditBarcodeRows(expData.treeNodeId)
      .then((rows) => {
        if (!cancelled) {
          setScreenEditBarcodeRows(rows);
        }
      })
      .catch((error) => {
        console.error('Failed to load screen barcodes for edit:', error);
        if (!cancelled) {
          setScreenEditBarcodeRows([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [expData?.treeNodeId, isScreenMode]);

  const experimentRows = buildEditBarcodeRows(
    expData?.plateDto,
    expData?.info?.screenLots,
  );
  const eligibleRows = isScreenMode ? screenEditBarcodeRows ?? [] : experimentRows;

  const isEditBarcodeDisabled =
    !isEditBarcodeAllowed ||
    (isScreenMode ? screenEditBarcodeRows === null : false) ||
    eligibleRows.length === 0;

  const openEditBarcodeModal = useCallback(async () => {
    let rows: EditBarcodeRow[] = [];

    try {
      rows = isScreenMode
        ? await loadScreenEditBarcodeRows(expData.treeNodeId)
        : await loadExperimentEditBarcodeRows(
            expData?.treeNodeId,
            expData?.plateDto,
            expData?.info?.screenLots,
          );
    } catch (error) {
      console.error('Failed to load current barcodes for edit:', error);
      rows = isScreenMode
        ? []
        : buildEditBarcodeRows(expData?.plateDto, expData?.info?.screenLots);
    }

    if (isScreenMode) {
      setScreenEditBarcodeRows(rows);
    }

    if (!rows.length) {
      return;
    }

    const onConfirmEditBarcodes = async (
      updates: { plateId: number; barcode: string }[],
    ) => {
      hideModal();
      showLoadingModal('Please wait');

      const response = await plateService.EditPlateBarcodesBatch(
        updates.map((update) => ({
          plateId: update.plateId,
          barcode: update.barcode,
        })),
      );

      hideModal();

      if (response) {
        dispatch(updatePlateBarcodes(updates));
        if (isScreenMode && expData?.treeNodeId) {
          loadScreenEditBarcodeRows(expData.treeNodeId).then(setScreenEditBarcodeRows);
        }
      } else {
        showModal({
          id: 'editbarcodebulk-modal-error',
          body: <EditBarcodeResponseModalBody />,
          heading: 'Error Barcode',
          okButtonText: 'Try Again',
          onOk: () => showEditBarcodeModalRef.current(),
        });
      }
    };

    showModal({
      id: 'editbarcodebulk-modal-edit',
      body: <EditBarcodeBulkModalBody rows={rows} modalHandlersRef={modalHandlersRef} />,
      showFooterSeparator: false,
      heading: 'Edit Barcode',
      showButtons: true,
      isCancelOnly: false,
      isOkOnly: false,
      okButtonText: 'CONFIRM',
      noButtonText: 'CANCEL',
      okayDisabled: true,
      onOk: () => {
        const updates = modalHandlersRef.current.getUpdates();
        if (updates.length > 0) {
          onConfirmEditBarcodes(updates);
        }
      },
      onCancel: hideModal,
    });
  }, [
    dispatch,
    expData?.info?.screenLots,
    expData?.plateDto,
    expData?.treeNodeId,
    hideModal,
    isScreenMode,
    showLoadingModal,
    showModal,
  ]);

  showEditBarcodeModalRef.current = openEditBarcodeModal;

  return {
    showEditBarcodeModal: openEditBarcodeModal,
    isEditBarcodeDisabled,
  };
}
